export type StarlinkTabId =
  | 'overview'
  | 'independent'
  | 'revenue'
  | 'investor'
  | 'products'
  | 'infrastructure'
  | 'value-chain'
  | 'suppliers'
  | 'key-data'
  | 'documents';

export type PeriodId = 'q2-2026' | 'h1-2026';

export type StarlinkKpi = {
  value: string;
  label: string;
  delta?: string;
  down?: boolean;
};

export type StarlinkLeaf = {
  id: string;
  label: string;
  detail: string;
  estimated?: boolean;
  glyph:
    | 'home'
    | 'car'
    | 'briefcase'
    | 'buildings'
    | 'ship'
    | 'plane'
    | 'truck'
    | 'landmark'
    | 'dish'
    | 'mini'
    | 'performance'
    | 'ellipsis';
};

export type StarlinkSegment = {
  id: string;
  label: string;
  revenue: number;
  display: string;
  shareLabel: string;
  color: string;
  accent: string;
  tile: string;
  leaves: StarlinkLeaf[];
};

export type StarlinkProductCard = {
  id: string;
  name: string;
  subtitle: string;
  detail: string;
  kind: 'standard' | 'mini' | 'performance' | 'maritime' | 'aviation' | 'direct';
};

export type StarlinkUpdate = {
  date: string;
  title: string;
  detail: string;
};

export type StarlinkQuarterPoint = {
  label: string;
  subscribersM: number | null;
  consumerM: number;
  enterpriseM: number;
  hardwareM: number;
  source: string;
};

export type StarlinkSnapshot = {
  periodId: PeriodId;
  periodLabel: string;
  headingPeriod: string;
  kpis: StarlinkKpi[];
  segments: StarlinkSegment[];
  connectivityDisplay: string;
  connectivityYoY: string;
};

export const STARLINK_TABS: Array<{ id: StarlinkTabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'independent', label: 'Independent series' },
  { id: 'revenue', label: 'Revenue & Segments' },
  { id: 'investor', label: 'Investor desk' },
  { id: 'products', label: 'Products' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'value-chain', label: 'Value Chain' },
  { id: 'suppliers', label: 'Suppliers' },
  { id: 'key-data', label: 'Key Data' },
  { id: 'documents', label: 'Documents' },
];

export const STARLINK_PERIODS: Array<{ id: PeriodId; label: string }> = [
  { id: 'q2-2026', label: 'Q2 2026' },
  { id: 'h1-2026', label: 'H1 2026' },
];

export const STARLINK_TAGLINE = 'Connectivity segment — Starlink service and kits';

export const STARLINK_DESCRIPTION =
  'Starlink broadband and mobile service plus the sale of Starlink Kits. Consumer is the Note 3 customer-type row for Starlink.com accounts; Enterprise & Government includes managed aviation, maritime, land mobility, fixed sites, government, and Starlink Mobile. All products revenue is Connectivity kits.';

const CONSUMER_LEAVES: StarlinkLeaf[] = [
  {
    id: 'personal',
    label: 'Personal (residential & roam)',
    detail: 'named in subscriber KPI · mix unprinted',
    estimated: true,
    glyph: 'home',
  },
  {
    id: 'self-serve',
    label: 'Self-serve business',
    detail: 'local / global priority · mix unprinted',
    estimated: true,
    glyph: 'briefcase',
  },
];

const ENTERPRISE_LEAVES: StarlinkLeaf[] = [
  {
    id: 'government',
    label: 'Government',
    detail: 'named in MD&A $939M walk · mix unprinted',
    estimated: true,
    glyph: 'landmark',
  },
  {
    id: 'aviation',
    label: 'Aviation',
    detail: 'named in MD&A $939M walk · mix unprinted',
    estimated: true,
    glyph: 'plane',
  },
  {
    id: 'maritime',
    label: 'Maritime',
    detail: 'named in MD&A $939M walk · mix unprinted',
    estimated: true,
    glyph: 'ship',
  },
  {
    id: 'other-enterprise',
    label: 'Other enterprise',
    detail: 'named in MD&A $939M walk · mix unprinted',
    estimated: true,
    glyph: 'buildings',
  },
  {
    id: 'mobile',
    label: 'Starlink Mobile',
    detail: 'Note 3 footnote 1 · parked in E&G',
    estimated: true,
    glyph: 'ellipsis',
  },
];

const HARDWARE_LEAVES: StarlinkLeaf[] = [
  {
    id: 'kits',
    label: 'Starlink Kits',
    detail: 'all Connectivity products · customer-type split unprinted',
    estimated: true,
    glyph: 'dish',
  },
];

const PERIODS: Record<
  PeriodId,
  {
    label: string;
    headingPeriod: string;
    connectivity: number;
    connectivityPrior: number;
    consumer: number;
    enterprise: number;
    hardware: number;
    subscribers: string;
    subscribersDelta: string;
    arpu: string;
    arpuDelta: string;
    arpuDown: boolean;
  }
> = {
  'q2-2026': {
    label: 'Q2 2026',
    headingPeriod: 'Q2 2026',
    connectivity: 4_291_000_000,
    connectivityPrior: 2_588_000_000,
    consumer: 2_485_000_000,
    enterprise: 1_806_000_000,
    hardware: 461_000_000,
    subscribers: '12.0M',
    subscribersDelta: '+101.2% YoY',
    arpu: '$66',
    arpuDelta: '−22.4% YoY',
    arpuDown: true,
  },
  'h1-2026': {
    label: 'H1 2026',
    headingPeriod: 'H1 2026',
    connectivity: 7_548_000_000,
    connectivityPrior: 5_062_000_000,
    consumer: 4_633_000_000,
    enterprise: 2_915_000_000,
    hardware: 841_000_000,
    subscribers: '12.0M',
    subscribersDelta: '+101.2% YoY',
    arpu: '$66',
    arpuDelta: '−22.6% YoY',
    arpuDown: true,
  },
};

function yoyPct(current: number, prior: number): string {
  const pct = ((current / prior - 1) * 100).toFixed(0);
  return `${Number(pct) > 0 ? '+' : ''}${pct}% YoY`;
}

function formatSegmentUsd(value: number): string {
  if (value >= 1_000_000_000) {
    const billions = value / 1_000_000_000;
    const decimals = billions >= 10 ? 1 : 3;
    return `$${billions.toFixed(decimals)}B`;
  }
  return `$${Math.round(value / 1_000_000)}M`;
}

function formatConnectivityUsd(value: number): string {
  return `$${(value / 1_000_000_000).toFixed(3)}B`;
}

function sharePct(part: number, whole: number): number {
  return Math.round((part / whole) * 100);
}

export function getStarlinkSnapshot(periodId: PeriodId): StarlinkSnapshot {
  const period = PERIODS[periodId];
  const total = period.connectivity;
  const consumerShare = sharePct(period.consumer, total);
  const enterpriseShare = 100 - consumerShare;
  const hardwareShare = sharePct(period.hardware, total);

  return {
    periodId,
    periodLabel: period.label,
    headingPeriod: period.headingPeriod,
    connectivityDisplay: formatConnectivityUsd(period.connectivity),
    connectivityYoY: yoyPct(period.connectivity, period.connectivityPrior),
    kpis: [
      {
        value: formatConnectivityUsd(period.connectivity),
        label: `Connectivity (${period.label})`,
        delta: yoyPct(period.connectivity, period.connectivityPrior),
      },
      {
        value: period.subscribers,
        label: 'Subscribers (EoP Jun 30)',
        delta: period.subscribersDelta,
      },
      {
        value: period.arpu,
        label: 'ARPU / month',
        delta: period.arpuDelta,
        down: period.arpuDown,
      },
      { value: '$4.42B', label: 'FY25 Connectivity op. income' },
      { value: '10,200+', label: 'Satellites (Jun 30)' },
    ],
    segments: [
      {
        id: 'consumer',
        label: 'Consumer',
        revenue: period.consumer,
        display: formatSegmentUsd(period.consumer),
        shareLabel: `${consumerShare}% of Connectivity`,
        color: '#3b82f6',
        accent: '#2563eb',
        tile: '#60a5fa',
        leaves: CONSUMER_LEAVES,
      },
      {
        id: 'enterprise',
        label: 'Enterprise & Government',
        revenue: period.enterprise,
        display: formatSegmentUsd(period.enterprise),
        shareLabel: `${enterpriseShare}% of Connectivity`,
        color: '#34d399',
        accent: '#059669',
        tile: '#6ee7b7',
        leaves: ENTERPRISE_LEAVES,
      },
      {
        id: 'hardware',
        label: 'Connectivity Products (Kits)',
        revenue: period.hardware,
        display: formatSegmentUsd(period.hardware),
        shareLabel: `${hardwareShare}% of Connectivity¹`,
        color: '#a78bfa',
        accent: '#7c3aed',
        tile: '#c4b5fd',
        leaves: HARDWARE_LEAVES,
      },
    ],
  };
}

export const STARLINK_TAKEAWAYS = [
  'Connectivity printed $4,291 million in Q2 2026 vs $2,588 million in Q2 2025 (+66% YoY). Consumer $2,485 million + Enterprise & Government $1,806 million = that total (SPCX 10-Q, Note 3 / Note 18).',
  'Consumer +44.4% YoY ($764 million). MD&A attributes that to 101.2% subscriber growth offset by a 22.4% ARPU decline from international expansion and lower-priced plans. 1.012 × 0.776 does not equal the 1.444 printed on the Consumer row.',
  'All products revenue is Connectivity kits: $461 million in Q2 vs $403 million a year ago (+14%). The filing does not split kits between Consumer and E&G.',
  'E&G $1,806 million vs $867 million (+108%). MD&A names the $939 million increase as government, aviation, maritime, and other enterprise. Starlink Mobile is parked in E&G by Note 3 footnote 1.',
  'Subscribers 12.0 million EoP (10.3 million at Mar 31 per EX-99.1) at $66 ARPU vs 6.0 million and $85 a year ago. 12.0 million × $66 × 3 months = $2,376 million, which is not the Consumer row.',
];

export const STARLINK_HISTORY: StarlinkQuarterPoint[] = [
  {
    label: "Q1'25",
    subscribersM: null,
    consumerM: 1_492,
    enterpriseM: 982,
    hardwareM: 352,
    source: 'H1 2025 − Q2 2025 (10-Q Note 3)',
  },
  {
    label: "Q2'25",
    subscribersM: 6.0,
    consumerM: 1_721,
    enterpriseM: 867,
    hardwareM: 403,
    source: 'SPCX 10-Q Note 3 / Key Business Metrics',
  },
  {
    label: "Q1'26",
    subscribersM: 10.3,
    consumerM: 2_148,
    enterpriseM: 1_109,
    hardwareM: 380,
    source: 'H1 2026 − Q2 2026 (10-Q); subscribers EX-99.1',
  },
  {
    label: "Q2'26",
    subscribersM: 12.0,
    consumerM: 2_485,
    enterpriseM: 1_806,
    hardwareM: 461,
    source: 'SPCX 10-Q Note 3 / Key Business Metrics',
  },
];

export const STARLINK_UPDATES: StarlinkUpdate[] = [
  {
    date: 'Aug 4, 2026',
    title: 'Q2 2026 10-Q and 8-K',
    detail: 'Connectivity $4,291 million (+66% YoY); Consumer $2,485 million; 12.0 million subscribers',
  },
  {
    date: 'May 12, 2026',
    title: 'FCC DA-26-471 EchoStar spectrum',
    detail: 'Two-step assignment; step one closed May 22 into Spectrum Business Trust 2025-1',
  },
  {
    date: 'Mar 31, 2026',
    title: '~9,600 operational satellites (424B4)',
    detail: '10-Q MD&A prints over 10,200 at June 30, 2026',
  },
];

export const STARLINK_PRODUCTS: StarlinkProductCard[] = [
  {
    id: 'standard-4x',
    name: 'Standard 4X',
    subtitle: 'Residential kit',
    detail: 'Current residential user terminal in the mapped value-chain graph. Kit dollars are the Connectivity products line; SKU mix is unprinted.',
    kind: 'standard',
  },
  {
    id: 'standard-4',
    name: 'Standard 4',
    subtitle: 'Residential kit',
    detail: 'Prior residential kit generation. Not a printed revenue line.',
    kind: 'standard',
  },
  {
    id: 'mini',
    name: 'Mini',
    subtitle: 'Portable kit',
    detail: 'Compact terminal. Public list exists; units and ASP are unprinted in the 10-Q.',
    kind: 'mini',
  },
  {
    id: 'performance',
    name: 'Performance',
    subtitle: 'High-duty kit',
    detail: 'Enterprise / remote-site terminal. Not broken out in Note 3.',
    kind: 'performance',
  },
  {
    id: 'maritime',
    name: 'Maritime',
    subtitle: 'Managed E&G',
    detail: 'Named in the MD&A E&G increase walk. Dollar mix of the $1,806 million is unprinted.',
    kind: 'maritime',
  },
  {
    id: 'aviation',
    name: 'Aviation',
    subtitle: 'Managed E&G',
    detail: 'Named in the MD&A E&G increase walk. Excluded from the self-serve subscriber KPI.',
    kind: 'aviation',
  },
  {
    id: 'direct-to-cell',
    name: 'Direct to Cell',
    subtitle: 'Starlink Mobile',
    detail: 'Note 3 footnote 1 parks Starlink Mobile in Enterprise & Government, not Consumer.',
    kind: 'direct',
  },
];

export const STARLINK_KEY_ROWS: Array<{ metric: string; value: string; source: string }> = [
  { metric: 'Connectivity Q2 2026', value: '$4,291 million', source: 'SPCX 10-Q, Note 18' },
  { metric: 'Connectivity Q2 2025', value: '$2,588 million', source: 'SPCX 10-Q, Note 18' },
  { metric: 'Consumer Q2 2026', value: '$2,485 million', source: 'SPCX 10-Q, Note 3' },
  { metric: 'Consumer Q2 2025', value: '$1,721 million', source: 'SPCX 10-Q, Note 3' },
  { metric: 'Consumer Q1 2026', value: '$2,148 million', source: 'H1 $4,633 − Q2 $2,485 (Note 3)' },
  { metric: 'Enterprise & Government Q2 2026', value: '$1,806 million', source: 'SPCX 10-Q, Note 3' },
  { metric: 'Enterprise & Government Q2 2025', value: '$867 million', source: 'SPCX 10-Q, Note 3' },
  { metric: 'Connectivity products (kits) Q2 2026', value: '$461 million', source: 'SPCX 10-Q, Note 3 (all Connectivity)' },
  { metric: 'Connectivity products Q2 2025', value: '$403 million', source: 'SPCX 10-Q, Note 3' },
  { metric: 'Connectivity H1 2026', value: '$7,548 million', source: 'SPCX 10-Q, Note 18' },
  { metric: 'FY 2025 Connectivity', value: '$11,387 million', source: 'SPCX 424(b)(4)' },
  { metric: 'Starlink subscribers Jun 30, 2026', value: '12.0 million EoP', source: 'SPCX 10-Q, Key Business Metrics' },
  { metric: 'Starlink subscribers Mar 31, 2026', value: '10.3 million EoP', source: 'SPCX EX-99.1 / 8-K' },
  { metric: 'Starlink subscribers Jun 30, 2025', value: '6.0 million EoP', source: 'SPCX 10-Q, Key Business Metrics' },
  { metric: 'ARPU Q2 / H1 2026', value: '$66 / month', source: 'SPCX 10-Q, Key Business Metrics' },
  { metric: 'ARPU Q2 / H1 2025', value: '$85 / month', source: 'SPCX 10-Q, Key Business Metrics' },
  { metric: 'Markets', value: '167', source: 'SPCX 10-Q, MD&A Overview' },
  { metric: 'Operational satellites Jun 30, 2026', value: '10,200+', source: 'SPCX 10-Q, MD&A Overview' },
  { metric: 'Operational satellites Mar 31, 2026', value: '~9,600', source: 'SPCX 424(b)(4)' },
];

export function isStarlinkLineId(id: string): boolean {
  return id === 'starlink' || id.startsWith('starlink-');
}
